import * as React from 'react';

import { gql, useQuery } from '@apollo/client';
import Loader from '../../../genericComponents/Loader';
import { Box, Chip, IconButton, useTheme } from '@mui/material';
import { ResponsiveChartContainer } from '@mui/x-charts';
import { DateTime } from "luxon";
import { BarPlot } from '@mui/x-charts/BarChart';
import { LinePlot } from '@mui/x-charts/LineChart';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { FileUpload } from '@mui/icons-material';
import DownloadMenu from './DownloadMenu';

// Formatter functie voor X-as
const xAxisFormatter = (value, context) => {
    if (!value) return '';
    
    // Als het een string is met underscores, format het
    if (typeof value === 'string' && value.includes('_')) {
        const parts = value.split('_');
        
        // Format afhankelijk van aantal delen
        if (parts.length === 2) {
            // MONTH format: "01_2024"
            const month = parseInt(parts[0]);
            const year = parseInt(parts[1]);
            const date = DateTime.utc(year, month, 1);
            return date.toFormat('MMM yyyy');
        } else if (parts.length === 3) {
            // DAY format: "15_01_2024"
            const day = parseInt(parts[0]);
            const month = parseInt(parts[1]);
            const year = parseInt(parts[2]);
            const date = DateTime.utc(year, month, day);
            return date.toFormat('dd MMM');
        } else if (parts.length === 4) {
            // HOUR/FIFTEEN_MINUTES format: "15_01_2024_14"
            const day = parseInt(parts[0]);
            const month = parseInt(parts[1]);
            const year = parseInt(parts[2]);
            const hour = parseInt(parts[3]);
            return `${hour}:00`;
        }
    }
    
    return value;
};

const TotalChart = ({ id, level, meterType, height, start, end,onTopLevelClick,groupLevel }) => {
    const defaultFilter= {start: start, end: end, groupLevel:groupLevel?groupLevel: "MONTH", label: start.c.year.toString(), lastFilter: null };
    React.useEffect(()=>{        
        if(currentFilter.start!=start||currentFilter.end!=end){
            setCurrentFilter(defaultFilter)
            return;
        }
    },[start,end])
    const ref=React.useRef();
    const [currentFilter, setCurrentFilter] = React.useState(defaultFilter);    
    const [activePill, setActivePill] = React.useState();
    const theme = useTheme();
    const GET_TOTAL = `query getTotalChart($id:UUID!,$level:Level!,$meterType:MeterType!,$start:DateTime!,$end:DateTime!,$groupLevel:TotalGraphGroupLevel!){
        totalGraphData(chartInput:{id: $id, level: $level,meterType:$meterType,start: $start,end:$end},groupLevel: $groupLevel){            
            values{
            temperature
            graaddag
            value
            contractedCapacity
            x
            }
        }
    }`;    
    const { data, loading, error } = useQuery(gql(GET_TOTAL),
        { variables: { id: id, level: level, meterType: meterType, start: currentFilter?.start, end: currentFilter?.end, groupLevel: currentFilter?.groupLevel } });
        
    if (loading || !data) {
        return <Loader height={height} text={"Loading Data"} />
    }  

    if (data.totalGraphData.values.length > 24*4+1) return <Box sx={{ height: "100%", width: "100%", textAlign: "center", alignContent: "center" }}>Whoops, too much data loaded, select less days to see this overview.</Box>
    
    // Filter out any undefined or null values
    const dataset = data.totalGraphData.values.filter(item => 
        item && item.x !== null && item.x !== undefined && item.value !== null && item.value !== undefined
    );
    
    // Check if we have valid data
    if (dataset.length === 0) {
        return <Box sx={{ height: "100%", width: "100%", textAlign: "center", alignContent: "center" }}>No data available for this period.</Box>
    }
    
    const isLowestLevel = currentFilter.groupLevel == "HOUR" || currentFilter.groupLevel == "FIFTEEN_MINUTES";
    const type = isLowestLevel ? "line" : "bar";
    
    const series = [
        { type: type, dataKey: 'value', color: theme.palette.secondary.main },
    ];
    
    // Safely add optional series
    if (!isLowestLevel && activePill == 0) {
        series.push({ type: 'line', color: theme.palette.primary.main, dataKey: 'graaddag', yAxisId: 'rightAxis' });
    } else if (!isLowestLevel && activePill == 1) {
        series.push({ type: 'line', color: theme.palette.primary.main, dataKey: 'temperature', yAxisId: 'rightAxis' });
    } else if (isLowestLevel && activePill == 2) {
        series.push({ type: 'line', color: theme.palette.primary.main, dataKey: 'contractedCapacity' });
    }
    const onItemClick = (event, d) => {
        const item = dataset[d.dataIndex];
        const splitted = item.x.split("_");
        switch (currentFilter.groupLevel) {
            case "MONTH":
                const month = parseInt(splitted[0]);
                const date = DateTime.utc(parseInt(splitted[1]), month, 1);
                const endDate = date.plus({ months: 1 }).minus({ days: 1 });
                setCurrentFilter({ start: date, end: endDate, groupLevel: "DAY", label: date.monthLong + " " + date.year, lastFilter: currentFilter });
                break;
            case "DAY":
                const day = parseInt(splitted[0]);
                const dayMonth = parseInt(splitted[1]);
                const dayYear = parseInt(splitted[2]);
                const dayDate = DateTime.utc(dayYear, dayMonth, day);
                const dayDateEnd = dayDate;                
                const groupLevel=meterType=="GAS" ? "HOUR" : "FIFTEEN_MINUTES";
                setCurrentFilter({ start: dayDate, end: dayDateEnd, groupLevel: groupLevel, label: dayDate.day + " " + dayDate.monthLong + " " + dayDate.year, lastFilter: currentFilter });
                break;
        }
    }
    const goToParent=()=>{
        if(onTopLevelClick){
            onTopLevelClick()
        }else{
            setCurrentFilter(currentFilter.lastFilter)
        }
        
    }      
    const unit =meterType=="GAS"?"m3" : "kWh";
    return (
        <Box sx={{ width: '100%', flexGrow: 1 }}>
            <Box sx={{ display: "flex", marginRight: theme.spacing(1), justifyContent: 'flex-end', width: "100%" }}>
                <Box>{currentFilter.lastFilter||onTopLevelClick?<IconButton onClick={() =>goToParent()} ><FileUpload/></IconButton>:null}</Box>
                {!isLowestLevel?<Box sx={{height:32, color:activePill == 0?"white":null, backgroundColor: activePill == 0 ? theme.palette.primary.main : null,mr: .5   }} onClick={() => setActivePill(activePill == 0 ? null : 0)} component={Chip} label={"Graaddagen"} />:null}
                {!isLowestLevel?<Box sx={{height:32, color:activePill == 1?"white":null,backgroundColor: activePill == 1 ? theme.palette.primary.main : null, mr: .5 }} onClick={() => setActivePill(activePill == 1 ? null : 1)} component={Chip} label={"Temperatuur"} />:null}
                {isLowestLevel&&meterType=="ELECTRICITY"?<Box sx={{height:32, color:activePill == 2?"white":null,backgroundColor: activePill == 2 ? theme.palette.primary.main : null, mr: .5 }} onClick={() => setActivePill(activePill == 2 ? null :2)} component={Chip} label={"Netcongestie"} />:null}
                <DownloadMenu graph={ref}  type="Total" data={dataset}/>                
            </Box>
            
            <ResponsiveChartContainer
                series={series}
                ref={ref}
                skipAnimation
                xAxis={[{
                    scaleType: 'band',
                    valueFormatter: (value, context) => xAxisFormatter(value, context),
                    dataKey: "x",
                    label: currentFilter.label,
                    categoryGapRatio: 0.7,
                    barGapRatio: 0.5
                }]
                }
                dataset={dataset}
                yAxis={[
                    { id: 'leftAxis', valueFormatter: (value, context) =>value+" "+unit },
                    { id: 'rightAxis' },
                ]}

                height={400}
                margin={{ left: 100, right: 70, bottom: 100 }}
                sx={{
                    [`.${axisClasses.left} .${axisClasses.label}`]: {
                        transform: 'translate(-25px, 0)',
                    },
                    [`.${axisClasses.right} .${axisClasses.label}`]: {
                        transform: 'translate(30px, 0)',
                    },
                }}
            >
                <BarPlot onItemClick={onItemClick} />
                <LinePlot />
                <ChartsXAxis />
                <ChartsYAxis axisId="leftAxis" />
                <ChartsYAxis
                    axisId="rightAxis"
                    position="right"
                    label={activePill == 0 ? "Graaddagen" : "Temperatuur"}
                />
                <ChartsGrid horizontal />
            </ResponsiveChartContainer>

        </Box>


    );
}

export default TotalChart;